// ConsultationFormStep.tsx
import React from "react";
import PatientInfoStep from "../steps/PatientInfoStep";
import MedicalConditionsStep from "../steps/MedicalConditionsStep";
import FamilyHistoryStep from "../steps/FamilyHistoryStep";
import CurrentConsultationStep from "../steps/CurrentConsultationStep";
import GynecologyFormStep from "../steps/GynecologyFormStep";
import PediatricsRegistrationStep from "../steps/PediatricsRegistrationStep";
import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

// ✅ Import des types centralisés
import type { Constante } from "../../../types/consultation";

interface ConsultationFormStepProps {
  onNext: () => void;
  onBack?: () => void;
  constanteData?: Constante;
}

export const ConsultationFormStep: React.FC<ConsultationFormStepProps> = ({
  onNext,
  onBack,
  constanteData,
}) => {
  const {
    formData,
    updateFormData,
  } = useConsultationWizardState(constanteData);

  // Récupérer le motif depuis constanteData ou formData
  const motif = constanteData?.reason || formData.patientInfo.motifConsultation;

  // Synchroniser motifConsultation avec constanteData si nécessaire
  React.useEffect(() => {
    if (constanteData?.reason) {
      const newMotif = constanteData.reason;
      if (newMotif && newMotif !== formData.patientInfo.motifConsultation) {
        updateFormData("patientInfo", "motifConsultation", newMotif);
      }
    }
  }, [constanteData, formData.patientInfo.motifConsultation, updateFormData]);

  return (
    <div className="space-y-8">
      {/* Section Informations du Patient */}
      <PatientInfoStep constanteData={constanteData} />

      {/* Section Conditions Médicales */}
      <MedicalConditionsStep />

      {/* Section Antécédents Familiaux */}
      <FamilyHistoryStep />

      {/* Section spécialisée pour Pédiatrie */}
      {(motif === "PEDIATRIC_CONSULTATION" ||
        motif === "Consultation pédiatrique") && (
          <PediatricsRegistrationStep />
        )}

      {/* Section spécialisée pour Gynécologie */}
      {(motif === "GYNECOLOGY_CONSULTATION" ||
        motif === "Consultation gynécologique") && (
          <GynecologyFormStep />
        )}

      {/* Section Consultation Actuelle */}
      <CurrentConsultationStep />

      {/* Boutons de navigation */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        {onBack ? (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Retour
          </button>
        ) : (
          <div></div>
        )}

        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
        >
          Suivant
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ConsultationFormStep;
